import React, { useState } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  Tag,
  Button,
  message,
  Tooltip,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  ArrowDownOutlined,
  ReloadOutlined,
  LinkOutlined,
  EditOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { bankTransactionsApi } from '../services/api';
import CheckEntryForm from '../components/financial/CheckEntryForm';
import CheckEditModal from '../components/financial/CheckEditModal';
import TransactionReviewPanel from '../components/financial/TransactionReviewPanel';
import { colors, spacing } from '../components/design-system/tokens';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface BankTransaction {
  id: string;
  transactionType: string;
  amount: number;
  date: string;
  reference: string | null;
  counterparty: string | null;
  description: string | null;
  status: string;
  checkImageDriveId?: string | null;
  checkImageUrl?: string | null;
  checkImageName?: string | null;
  xeroPaymentId?: string | null;
  allocations?: Array<{
    id: string;
    amount: number;
    shipment?: { reference: string };
  }>;
}

const CheckEntry: React.FC = () => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<BankTransaction | null>(null);
  const { data: transactionsData, isLoading, refetch } = useQuery({
    queryKey: ['bank-transactions', 'checks'],
    queryFn: async () => {
      return bankTransactionsApi.list({
        transactionType: 'CHECK_IN',
        limit: 50,
      });
    },
  });

  const rawData = (transactionsData as any)?.data || transactionsData;
  const transactions = (rawData?.transactions || []).filter(
    (t: any) => t.transactionType === 'CHECK_IN'
  );

  const totals = transactions.reduce(
    (acc: any, t: any) => {
      acc.total += Number(t.amount);
      acc.count++;
      return acc;
    },
    { total: 0, count: 0 }
  );

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Check #',
      dataIndex: 'reference',
      key: 'reference',
      width: 100,
      render: (ref: string) => <Text code>{ref}</Text>,
    },
    {
      title: 'From',
      dataIndex: 'counterparty',
      key: 'counterparty',
      width: 180,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong style={{ color: colors.status.positive }}>
          +${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusColors: Record<string, string> = {
          UNRECONCILED: 'orange',
          PARTIAL: 'blue',
          RECONCILED: 'green',
          PUSHED: 'cyan',
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Allocations',
      dataIndex: 'allocations',
      key: 'allocations',
      width: 120,
      render: (allocations: any[]) => {
        if (!allocations || allocations.length === 0) {
          return <Text type="secondary">Unallocated</Text>;
        }
        return (
          <Tooltip
            title={allocations.map((a) => a.shipment?.reference || a.shipmentId).join(', ')}
          >
            <Tag color="blue" icon={<LinkOutlined />}>
              {allocations.length} shipment{allocations.length > 1 ? 's' : ''}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc: string) =>
        desc ? <Text type="secondary">{desc}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: any, record: BankTransaction) => (
        <Space>
          {record.checkImageUrl && (
            <Tooltip title="Has check image">
              <FileImageOutlined style={{ color: colors.primary[60] }} />
            </Tooltip>
          )}
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedTransaction(record);
              setEditModalOpen(true);
            }}
          />
        </Space>
      ),
    },
  ];

  const handleSuccess = () => {
    refetch();
    message.success('Check recorded successfully');
  };

  return (
    <div style={{ padding: spacing.xl }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Check Payments
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <CheckEntryForm onSuccess={handleSuccess} />
          </Space>
        </div>

        <TransactionReviewPanel onResolve={() => refetch()} />

        <Row gutter={16}>
          <Col span={12}>
            <Card>
              <Statistic
                title="Total Received"
                value={totals.total}
                precision={2}
                prefix="$"
                valueStyle={{ color: colors.status.positive }}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({totals.count} checks)
                  </Text>
                }
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <Statistic
                title="Checks This Month"
                value={transactions.filter((t: any) => 
                  dayjs(t.date).isAfter(dayjs().startOf('month'))
                ).length}
                prefix={<ArrowDownOutlined style={{ color: colors.status.positive }} />}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Recent Check Payments">
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${total} check payments`,
            }}
            size="middle"
          />
        </Card>
      </Space>

      {selectedTransaction && (
        <CheckEditModal
          transaction={selectedTransaction}
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedTransaction(null);
          }}
          onSuccess={() => {
            refetch();
            setEditModalOpen(false);
            setSelectedTransaction(null);
          }}
        />
      )}
    </div>
  );
};

export default CheckEntry;
